import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useMoveAgreementToGroupMutation,
  FakturowniaInvoicesDocument,
  StudentDetailDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

interface IChangeAgreementGroupArgs {
  agreementId: string;
  newGroupId: string;
  effectiveDate: string;
  copyPayments?: boolean;
  copyContributions?: boolean;
}

interface IUseChangeAgreementGroupHook {
  move: (args: IChangeAgreementGroupArgs) => Promise<boolean>;
  loading: boolean;
}

/**
 * Phase 13 follow-up §6: thin wrapper around `changeAgreementGroup` with
 * toasts + a `StudentDetail` refetch so the card repaints with the new
 * agreement and the old one greyed out. Mirrors the toast vocabulary of
 * `usePaymentMutations` so the financier sees a consistent UX.
 *
 * Cache strategy: full refetch of `StudentDetail`. The mutation rewires
 * two agreements + their payments + their invoices on the server; an
 * Apollo cache patch on this scale is more error-prone than worth it.
 */
export const useChangeAgreementGroup = (
  studentId?: string,
): IUseChangeAgreementGroupHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const refetchQueries = useMemo(() => {
    const queries: Array<
      | { query: typeof StudentDetailDocument; variables: { id: string } }
      | { query: typeof FakturowniaInvoicesDocument }
    > = [{ query: FakturowniaInvoicesDocument }];
    if (studentId) {
      queries.push({
        query: StudentDetailDocument,
        variables: { id: studentId },
      });
    }
    return queries;
  }, [studentId]);

  const [mutation, state] = useMoveAgreementToGroupMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const notifySuccess = useCallback(
    (title: string) => {
      addToast({
        data: { type: ToastTypes.success, title },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast],
  );

  const notifyError = useCallback(
    (
      title: string,
      errors?: Array<{
        message?: string | null;
        code?: string | null;
      } | null> | null,
    ) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title,
          text:
            errors
              ?.map((e) => e?.message || e?.code || null)
              .filter(Boolean)
              .join('\n') || undefined,
        },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast],
  );

  const move = useCallback(
    async (args: IChangeAgreementGroupArgs) => {
      try {
        const result = await mutation({
          variables: { input: args },
        });
        const payload = result.data?.changeAgreementGroup;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.moveGroup.toasts.moved'));
          return true;
        }
        notifyError(
          t('pages.studentDetail.moveGroup.toasts.moveFailed'),
          payload?.customErrors,
        );
        return false;
      } catch (error) {
        notifyError(t('pages.studentDetail.moveGroup.toasts.moveFailed'), [
          { message: (error as Error).message, code: null },
        ]);
        return false;
      }
    },
    [mutation, notifySuccess, notifyError, t],
  );

  return { move, loading: state.loading };
};
